/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { createDefaultGridModel, ROW_DELETED_CLASS } from '@gh/common-internal-shared';
import { getRouteInitData } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildEditableRadioColumn,
  buildEditableSelectColumn,
  EditableCellOptionSet,
  FocusedCell,
  GridDefinition,
  GridDefinitionFactory,
  rowDragMoveEventArrayFactory,
} from '@gh/core-ui-grid';
import {
  CpStage,
  cpStageArr,
  cpStageFieldState,
  getCpStageId,
  validateCpStageField,
} from '../../../resources/scheduling-cp-details.model';

@Component({
  selector: 'gh-scheduling-cp-details-stage-list',
  template: require('./scheduling-cp-details-stage-list.component.html'),
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchedulingCpDetailsStageListComponent implements OnInit {
  @Input() editable = true;
  @Input() stages: CpStage[];
  @Input() visibleStages: CpStage[];
  @Input() showDeleted: boolean;
  @Input() selected?: CpStage;
  @Input() focusedCell?: FocusedCell;

  @Output() add = new EventEmitter<void>();
  // tslint:disable-next-line:no-output-named-after-standard-event
  @Output() open = new EventEmitter<CpStage>();
  @Output() remove = new EventEmitter<CpStage>();
  @Output() unremove = new EventEmitter<CpStage>();
  @Output() move = new EventEmitter<{ from: CpStage; to: CpStage }>();
  @Output() slabChange = new EventEmitter<CpStage>();
  @Output() substitutorChange = new EventEmitter<{ deleted: CpStage; substitutor: ID }>();
  @Output() selectedChange = new EventEmitter<CpStage>();
  @Output() focusedCellChange = new EventEmitter<FocusedCell>();
  @Output() showDeletedChange = new EventEmitter<boolean>();

  gridDefinition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private gridDefinitionFactory: GridDefinitionFactory) {
  }

  ngOnInit(): void {
    this.gridDefinition = this.buildGridDefinition();
  }

  onShowDeletedChange(showDeleted: boolean): void {
    this.showDeletedChange.emit(showDeleted);
  }

  private buildGridDefinition(): GridDefinition {
    const { cpStagesLayout } = getRouteInitData(this.route);
    const model = createDefaultGridModel({
      editable: this.editable,
      getId: getCpStageId,
      validate: validateCpStageField,
      getCellDef: cpStageFieldState,
      command: (entity, name, value) => {
        if (name === 'substituteId') {
          this.substitutorChange.emit({ deleted: entity, substitutor: value });
        }
      },
    });
    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      model,
      layout: cpStagesLayout,
      options: <any>{
        rowSelection: 'single',
        animateRows: true,
        defaultColDef: {
          sortable: false,
        },
        rowClassRules: {
          [ROW_DELETED_CLASS]: ({ data }: { data: CpStage }) => !data.isActive,
        },
        onRowDragMove: rowDragMoveEventArrayFactory<CpStage>((from, to) => this.move.emit({ from, to })),
      },
      columns: {
        name: {
          rowDrag: this.editable,
        },
        isSlab: buildEditableRadioColumn(model.getCellAccessor('isSlab'), {
          onChange: (params) => this.slabChange.emit(params.data),
        }),
        substituteId: buildEditableSelectColumn(this.buildCpStageOptionSet(), model.getCellAccessor('substituteId')),
      },
    });
  }

  private buildCpStageOptionSet(): EditableCellOptionSet {
    return {
      getOptions: (params) => this.stages
        .filter((stage) => stage.isActive || getCpStageId(stage) === params.data.substituteId)
        .map((stage) => ({ id: getCpStageId(stage), name: stage.name })),
      format: (params, value) => {
        const stage = cpStageArr.find(this.stages, value);
        return stage ? stage.name : '';
      },
      hasOptions: () => this.visibleStages.length > 0,
    };
  }
}
